/**
 * BadgeShareKitModal — the completion-certificate share kit.
 *
 * Opens from the prominent "Share your win" button on each earned badge
 * inside AchievementsStrip. Shows a preview of the auto-generated PNG
 * (`/api/lms/badge-cards/{id}.png` — the existing share card renderer)
 * plus 4 primary actions: Download PNG, Copy link, Share to LinkedIn,
 * Share to X. Same PNG URL is embedded in the course-completion nudge
 * email so the two flows pair.
 */
import React, { useContext, useState } from 'react';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { X, Download, Copy, Linkedin, Twitter, Award } from 'lucide-react';

export default function BadgeShareKitModal({ open, onClose, badge }) {
  const { API, user } = useContext(AuthContext);
  const [busy, setBusy] = useState(null);

  if (!open || !badge) return null;

  const handle = user?.handle || user?.id || '';
  const publicUrl = `${window.location.origin}/u/${handle}?badge=${badge.id}`;
  const pngUrl = `${API}/lms/badge-cards/${badge.id}.png`;
  const shareText = `I just earned the "${badge.course_title}" badge on FDAcademy 🎉`;

  const doDownload = () => {
    setBusy('download');
    const a = document.createElement('a');
    a.href = pngUrl;
    a.download = `${(badge.course_title || 'badge').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-badge.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('Badge PNG downloaded');
    setBusy(null);
  };

  const doCopy = async () => {
    setBusy('copy');
    try { await navigator.clipboard.writeText(publicUrl); toast.success('Share link copied'); }
    catch { toast.error('Copy failed'); }
    setBusy(null);
  };

  const doLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(publicUrl)}`,
      '_blank', 'noopener,noreferrer',
    );
  };

  const doX = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(publicUrl)}`,
      '_blank', 'noopener,noreferrer',
    );
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-navy-950/70 backdrop-blur-sm px-4"
      data-testid="badge-share-kit-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-card border border-border shadow-2xl overflow-hidden max-h-[92dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2 p-5 pb-3">
          <div className="h-9 w-9 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
            <Award className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Share your win</div>
            <h3 className="font-heading text-lg font-extrabold text-foreground">
              I finished {badge.course_title}
            </h3>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Downloadable badge card sized for LinkedIn, X, Slack and email.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center"
            aria-label="Close"
            data-testid="badge-share-kit-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Preview — the actual PNG the backend will hand out. */}
        <div className="px-5">
          <div className="rounded-xl overflow-hidden border border-border bg-navy-950">
            <img
              src={pngUrl}
              alt={`${badge.course_title} share card preview`}
              className="w-full block"
              data-testid="badge-share-kit-preview"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 p-5">
          <Button
            onClick={doDownload}
            disabled={busy === 'download'}
            className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="badge-share-kit-download"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> Download PNG
          </Button>
          <Button
            variant="outline"
            onClick={doCopy}
            disabled={busy === 'copy'}
            className="rounded-full font-bold"
            data-testid="badge-share-kit-copy"
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link
          </Button>
          <Button
            variant="outline"
            onClick={doLinkedIn}
            className="rounded-full font-bold border-[#0A66C2]/40 text-[#0A66C2] hover:bg-[#0A66C2]/5"
            data-testid="badge-share-kit-linkedin"
          >
            <Linkedin className="w-3.5 h-3.5 mr-1.5" /> LinkedIn
          </Button>
          <Button
            variant="outline"
            onClick={doX}
            className="rounded-full font-bold"
            data-testid="badge-share-kit-x"
          >
            <Twitter className="w-3.5 h-3.5 mr-1.5" /> Post to X
          </Button>
        </div>

        <div className="px-5 pb-4 text-[10px] text-muted-foreground italic">
          Everyone who clicks your share link lands on your public profile with this badge front-and-center.
        </div>
      </div>
    </div>
  );
}
